import { useState, useEffect, useCallback } from 'react';

const SUPPORTED_LOCALES = ['en', 'it', 'fr'];
const FALLBACK = 'en';

function resolveLocale() {
  if (typeof window === 'undefined') return FALLBACK;
  const raw = window.Shopify?.locale;
  if (raw && SUPPORTED_LOCALES.includes(raw)) return raw;
  return FALLBACK;
}

export default function useTranslate() {
  const [translations, setTranslations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locale, setLocale] = useState(resolveLocale);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    import(`../i18n/${locale}.json`)
      .then((mod) => {
        if (!cancelled) {
          setTranslations(mod.default || mod);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Fallback to it if the locale file doesn't exist
          import('../i18n/it.json')
            .then((mod) => {
              setTranslations(mod.default || mod);
            })
            .catch(() => {
              setTranslations({});
            })
            .finally(() => {
              if (!cancelled) setLoading(false);
            });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const t = useCallback(
    (key, params = {}) => {
      if (!translations) return key;
      const keys = key.split('.');
      let value = translations;
      for (const k of keys) {
        if (value == null) break;
        value = value[k];
      }
      if (typeof value === 'string') {
        return value.replace(/\{\s*(\w+)\s*\}/g, (_, p) => (params[p] != null ? params[p] : `{${p}}`));
      }
      if (Array.isArray(value)) return value;
      return key;
    },
    [translations],
  );

  return { t, loading, locale };
}
